import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types'; // Import PropTypes

import { Button, Box, Drawer, Grid, Stack, Typography } from '@mui/material';

import SeekerOffersMenu from './SeekerOffersMenu';
import SeekerUserOfferCard from './SeekerUserOfferCard';
import SeekerSystemOfferCard from './SeekerSystemOfferCard';
import SeekerAdvisorOfferCard from './SeekerAdvisorOfferCard';
import SeekerCallBenefitCard from './SeekerCallBenefitCard';
import SeekerFeedBenefitCard from './SeekerFeedBenefitCard';
import SeekerVolleyBenefitCard from './SeekerVolleyBenefitCard';
import useListingDetailsAndFeedback from 'app/hooks/useListingDetailsAndFeedback';
import CheckboxChip from 'app/components/CheckboxChip';

import { featureFlag } from 'app/util/featureFlag';
import { useGetSeekerOffersQuery } from 'app/features/account/api/accountApi';

const OFFER_TYPE_TO_CARD = {
  UserOffer: SeekerUserOfferCard,
  SystemOffer: SeekerSystemOfferCard,
  AdvisorOffer: SeekerAdvisorOfferCard,
  CallBenefit: SeekerCallBenefitCard,
  FeedBenefit: SeekerFeedBenefitCard,
  VolleyBenefit: SeekerVolleyBenefitCard,
};

const EmptyState = (filter) => (
  <Box
    sx={{
      textAlign: 'center',
      py: 2,
    }}
  >
    <Typography variant="subtitle1">
      No&nbsp;
      {filter === 'Packages' ? 'packages' : 'offers'}
      &nbsp;available right now.
    </Typography>
    <Typography variant="body1" sx={{ mb: 2 }}>
      Check back soon!
    </Typography>
    <Link to="/search">
      <Button variant="contained" size="small" color="secondary" sx={{ minWidth: '138px', fontSize: '0.938em' }}>
        Find a Flirt
      </Button>
    </Link>
  </Box>
);

const SeekerOffersDrawer = ({ open, onClose }) => {
  const [filter, setFilter] = useState('All');
  const [sort, setSort] = useState('Newest First');

  const offerResult = useGetSeekerOffersQuery();
  const offers = offerResult?.data?.offers || [];

  const {
    onListingDetailsClick,
    onListingFeedbackClick,
  } = useListingDetailsAndFeedback({});

  const visibleOffers = offers.filter((offer) => {
    if (filter === 'Packages') {
      return offer.type.match(/Benefit/);
    } else if (filter === 'Offers') {
      return !offer.type.match(/Benefit/);
    }
    return true;
  }).sort(
    (a, b) => (
      (sort === 'Newest First') ?
        (new Date(b.created_at) - new Date(a.created_at)) :
        (new Date(a.expires_at) - new Date(b.expires_at))
    ),
  );

  const list = (
    <Grid container className="drawer-max-width">
      <Grid container className="drawer-hdr">
        <Grid item xs={11}>
          <span className="subhead">Offers & Promotions</span>
        </Grid>
        <Grid item xs={1} className="pull-right">
          <a role="link" tabIndex={0} onClick={onClose} onKeyDown={onClose}>
            <img src="/plus_frontend/assets/svg/x-close.svg" alt="close" />
          </a>
        </Grid>
      </Grid>
      <Box sx={{ backgroundColor: 'var(--body_background)', width: '100%', px: 2, py: 1, mb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box display="flex" justifyContent="start" my={1.5}>
            { featureFlag.enabled('RECURRING_PACKAGE_TRIBUTES_19537') && (
              <>
                <CheckboxChip label="All" value="All" state={filter} setState={setFilter} />
                <CheckboxChip label="Offers" value="Offers" state={filter} setState={setFilter} />
                <CheckboxChip label="Packages" value="Packages" state={filter} setState={setFilter} />
              </>
            )}
          </Box>
          <SeekerOffersMenu sort={sort} setSort={setSort} />
        </Stack>
      </Box>
      <Grid container sx={{ mb: 10, lineHeight: '1.5em' }}>
        <Grid item xs={12} sx={{ mb: 2, mt: 0.25 }}>
          {visibleOffers.length === 0 && (
            <EmptyState filter={filter} />
          )}
          {visibleOffers.length > 0 && visibleOffers.map((offer) => {
            const CardComponent = OFFER_TYPE_TO_CARD[offer.type];
            if (!CardComponent) {
              return null;
            }
            return (
              <CardComponent
                key={`${offer.type}_${offer.id}`}
                offer={offer}
                onDetailsClick={(evt) => { if (offer.listing) { onListingDetailsClick(offer.listing)(evt); onClose(); } }}
                onFeedbackClick={(evt) => { if (offer.listing) { onListingFeedbackClick(offer.listing)(evt); onClose(); } }}
              />
            );
          })}
        </Grid>
      </Grid>
    </Grid>
  );

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      {list}
    </Drawer>
  );
};

SeekerOffersDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default SeekerOffersDrawer;
